import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PsychologyIcon from '@mui/icons-material/Psychology';

/** Display label for a participant in the picker + the selected title —
 *  name, else email, else a placeholder. */
function participantLabel(p: Participant): string {
  return p.full_name ?? (p.has_email ? p.email : 'No name');
}

import * as cognitiveApi from '../api/cognitive';
import type { CaptureSession } from '../api/sessions';
import type { Participant } from '../api/tenants';
import { useAuth, canOperateTenant, canWriteTenant } from '../auth/AuthContext';
import { RecordingFilters } from './RecordingFilters';
import {
  EMPTY_FILTER,
  filterOptions,
  isFilterActive,
  matchesFilter,
} from './recordingFilterRules';
import { useFilterParams } from './useFilterParams';
import {
  mergeOptions,
  toSessionQuery,
  useDebounced,
  useSessionFilterOptions,
} from './serverFilter';
import { CompareView } from './CompareView';
import {
  CaptureTimeline,
  buildTimelineEvents,
  pipelineGroups,
  withPipelines,
  type TimelineEvent,
} from './CaptureTimeline';
import { CognitiveTrace } from './CognitiveTrace';
import { SessionMetricScorecard } from './SessionMetricScorecard';
import { HandTrajectoryPanel } from './HandTrajectoryPanel';
import { AiCoachCard } from './AiCoachCard';
import { useInsightsPolling } from '../hooks/useInsightsPolling';
import { useParticipantActivity } from '../hooks/useParticipantActivity';
import { useCaptureActions } from '../hooks/useCaptureActions';
import { useSessionPipelines } from '../hooks/useSessionPipelines';

/**
 * Tenant Dashboard body — Individual (default) + Compare modes.
 *
 * Individual: pick one participant → their activity timeline (same source as
 * the Activity page, via useParticipantActivity) → per-session metric
 * scorecard. No blended tenant-wide averages — a metric only means something
 * within one procedure, and a session IS one procedure, so scoping is
 * implicit here.
 *
 * Compare: head-to-head A-vs-B (participant or group), procedure-scoped —
 * where aggregate/contrast metrics live (built out in a later phase).
 */
interface Props {
  tenantId: string;
  participants: Participant[];
  /** Auto-select this participant on first load (the one with the most
   *  sessions) so the Individual view lands on populated data. */
  defaultParticipantId?: string | null;
  /** View mode — owned by the parent so the Individual/Compare toggle can
   *  sit in the page header next to Refresh. */
  mode: 'individual' | 'compare';
}

export function TenantCognitiveSummary({
  tenantId,
  participants,
  defaultParticipantId,
  mode,
}: Props) {
  const [selected, setSelected] = useState<{
    userId: string;
    label: string;
  } | null>(null);

  // Active participants, sorted alphabetically by the same label the picker
  // shows (name, else email) so the dropdown is scannable instead of in the
  // API's arbitrary order.
  const roster = useMemo(
    () =>
      participants
        .filter((p) => p.is_active)
        // Alphabetical by exactly the label the picker shows.
        .sort((a, b) =>
          participantLabel(a).localeCompare(participantLabel(b), undefined, {
            sensitivity: 'base',
            numeric: true,
          }),
        ),
    [participants],
  );

  // Apply a default participant ONCE — never override a choice the operator
  // has already made. Only ever land on the participant the parent points at
  // (the one with the most recorded sessions). We deliberately do NOT fall
  // back to roster[0]: `defaultParticipantId` is undefined while the parent's
  // sessions are still loading, so a fallback would lock onto a data-less
  // participant before the real one resolves (the bug where the dashboard
  // never defaulted to the participant that had data). When it stays null
  // (loaded, nobody has data) the panel is left empty by design.
  const [defaultApplied, setDefaultApplied] = useState(false);

  // Reset the auto-selection when the TENANT changes (an admin switching
  // tenants). Done during render — React's "adjust state on a prop change"
  // pattern — so we never flash the previous tenant's participant. Without
  // this, `selected` + `defaultApplied` stay latched on the old tenant and
  // the new tenant never re-runs the default-apply below, so it never lands
  // on its own data participant (#287 fixed the first load but NOT a switch).
  const [seenTenant, setSeenTenant] = useState(tenantId);
  if (tenantId !== seenTenant) {
    setSeenTenant(tenantId);
    setSelected(null);
    setDefaultApplied(false);
  }

  useEffect(() => {
    if (defaultApplied || selected || roster.length === 0) return;
    // undefined (sessions still loading) or null (nobody has data) → wait /
    // stay empty rather than pick someone arbitrary.
    if (!defaultParticipantId) return;
    const p = participants.find((x) => x.id === defaultParticipantId);
    if (p) {
      setSelected({
        userId: p.id,
        label: p.full_name ?? (p.has_email ? p.email : p.id),
      });
      setDefaultApplied(true);
    }
  }, [defaultParticipantId, participants, roster, defaultApplied, selected]);

  return (
    <Stack spacing={3}>
      {mode === 'individual' ? (
        <Stack spacing={2}>
          {selected ? (
            <ParticipantDrill
              tenantId={tenantId}
              participantUserId={selected.userId}
              roster={roster}
              onSelectParticipant={(p) =>
                setSelected({
                  userId: p.id,
                  label: p.full_name ?? (p.has_email ? p.email : p.id),
                })
              }
            />
          ) : defaultParticipantId === undefined ? (
            // Sessions still loading — don't flash the empty state before we
            // know whether anyone has data.
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Alert severity="info" variant="outlined">
              {roster.length === 0
                ? 'No active participants in this tenant yet.'
                : 'No recorded sessions yet — participant data appears here once a session is captured.'}
            </Alert>
          )}
        </Stack>
      ) : (
        <CompareView tenantId={tenantId} participants={participants} />
      )}
    </Stack>
  );
}

function ParticipantDrill({
  tenantId,
  participantUserId,
  roster,
  onSelectParticipant,
}: {
  tenantId: string;
  participantUserId: string;
  /** Active participants, for the header picker. */
  roster: Participant[];
  onSelectParticipant: (p: Participant) => void;
}) {
  const { user } = useAuth();
  const canWrite = canWriteTenant(user, tenantId);
  const [showDisabled, setShowDisabled] = useState(false);
  // Sessions + baselines come from the SAME tenant-wide feeds the Activity
  // page reads (filtered to this participant), so the two lists can't drift.
  // Declared before the fetch because the fetch now consumes it: the
  // narrowing runs in SQL, not over the response.
  // In the URL, so Back undoes a filter instead of leaving the page.
  const [filter, setFilter] = useFilterParams('act');
  const serverFilter = useDebounced(filter);
  const filterSource = useSessionFilterOptions(tenantId, showDisabled);
  const { sessions, error, reload } = useParticipantActivity(
    tenantId,
    participantUserId,
    showDisabled,
    toSessionQuery(serverFilter, filterSource.participantIds),
  );
  // Same per-recording action menu (download / attach / replay / split / tag /
  // disable / restore) the Activity page uses — shared hook, so the two
  // surfaces can't drift.
  const {
    actions,
    dialogs,
    error: actionError,
  } = useCaptureActions({ tenantId, canWrite, onChanged: reload });
  const [scores, setScores] = useState<cognitiveApi.CognitiveScore[] | null>(
    null,
  );
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );

  // Cognitive scores drive the trace card; the timeline itself comes from the
  // shared participant-activity hook above.
  useEffect(() => {
    let cancelled = false;
    setScores(null);
    setSelectedSessionId(null);
    cognitiveApi
      .listParticipantCognitiveScores(tenantId, participantUserId)
      .then((c) => {
        if (!cancelled) setScores(c);
      })
      .catch(() => {
        if (!cancelled) setScores([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, participantUserId]);

  // Sessions only. Baseline recordings are calibration, not performance —
  // they belong on the Activity page (the operational list), not on the
  // dashboard that reads a participant's results.
  const allEvents: TimelineEvent[] = useMemo(
    () => (sessions ? buildTimelineEvents(sessions, []) : []),
    [sessions],
  );
  // Same filter vocabulary as Activity and the participant dashboard. This
  // timeline is already scoped to ONE participant (picked above), on whatever
  // device they used, so only procedure and date/time are worth asking here.
  const filterable = useMemo(
    () =>
      allEvents.map((e) => ({
        participantLabel: e.participantLabel,
        deviceLabel: e.deviceLabel,
        procedureName: e.procedureName,
        startedAt: e.capturedAt,
      })),
    [allEvents],
  );
  // The server's lists span this tenant's whole history; the rows' own
  // values are merged in so nothing listed is missing from the filter.
  const filterOpts = useMemo(
    () => mergeOptions(filterSource.options, filterOptions(filterable)),
    [filterSource.options, filterable],
  );
  const events: TimelineEvent[] = useMemo(
    () =>
      allEvents.filter((e) =>
        matchesFilter(
          {
            participantLabel: e.participantLabel,
            deviceLabel: e.deviceLabel,
            procedureName: e.procedureName,
            startedAt: e.capturedAt,
          },
          filter,
        ),
      ),
    [allEvents, filter],
  );

  // Where each listed session's data is (SESSION-PIPELINE): dots on the
  // tiles, and the strip on the selected session's trace card.
  const groups = useMemo(() => pipelineGroups(events, tenantId), [events, tenantId]);
  const pipelines = useSessionPipelines('tenant', groups);
  const shownEvents = useMemo(() => withPipelines(events, pipelines), [events, pipelines]);

  // Pre-select the first (most recent) session once this participant's
  // timeline loads, so the scorecard + trace are visible immediately. Once
  // per participant — after that the operator drives selection (and can
  // deselect). The `fresh` guard skips the stale render right after a
  // participant switch, where `sessions` is still the previous participant's.
  const autoSelectedFor = useRef<string | null>(null);
  useEffect(() => {
    if (autoSelectedFor.current === participantUserId) return;
    if (sessions === null) return;
    const fresh =
      sessions.length === 0 ||
      sessions[0].participant_user_id === participantUserId;
    if (!fresh) return;
    const firstSession = events.find((e) => e.kind === 'session');
    if (firstSession) setSelectedSessionId(firstSession.id);
    autoSelectedFor.current = participantUserId;
  }, [sessions, events, participantUserId]);

  // Smart-polling: while any session for this participant has no
  // cognitive_score row yet, refetch the scores feed every 7 s so
  // the trace card lights up as soon as the worker finishes.
  const drillInsightsPending = useMemo(() => {
    if (sessions === null || scores === null) return false;
    if (sessions.length === 0) return false;
    const scored = new Set(scores.map((c) => c.session_id));
    return sessions.some((s) => !scored.has(s.session_id));
  }, [sessions, scores]);
  // Re-fetch the scores feed on demand — the trace card's smoothing slider
  // calls this after a re-bin so the new interval (and its rebuilt trace)
  // is picked up as the retrace job lands (COGNITIVE-TRACE-SMOOTHING).
  const reloadScores = useCallback(() => {
    void cognitiveApi
      .listParticipantCognitiveScores(tenantId, participantUserId)
      .then(setScores)
      .catch(() => {});
  }, [tenantId, participantUserId]);
  const { polling: drillInsightsPolling } = useInsightsPolling({
    pending: drillInsightsPending,
    onTick: reloadScores,
  });

  function handleSelect(event: TimelineEvent) {
    if (event.kind !== 'session') return;
    setSelectedSessionId((prev) => (prev === event.id ? null : event.id));
  }

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedSessionId) ?? null,
    [events, selectedSessionId],
  );
  const selectedSession =
    selectedEvent && selectedEvent.kind === 'session'
      ? (selectedEvent.raw as CaptureSession)
      : null;

  // The Autocomplete's controlled value — the participant being viewed. Falls
  // back to the first of the roster on a transient id/roster mismatch (e.g.
  // a tenant switch) so the picker always has a non-null value.
  const current =
    roster.find((x) => x.id === participantUserId) ?? roster[0];

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            <PsychologyIcon color="primary" />
            <Stack>
              {/* The panel title IS the participant picker — switch subject
                  here rather than in a separate band above. Searchable so a
                  long roster is a type-to-filter, not a long scroll. */}
              {current && (
                <Autocomplete
                  options={roster}
                  value={current}
                  onChange={(_, p) => {
                    if (p) onSelectParticipant(p);
                  }}
                  getOptionLabel={participantLabel}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  disableClearable
                  size="small"
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search participant…"
                      aria-label="Participant"
                      slotProps={{
                        input: { ...params.InputProps, disableUnderline: true },
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontWeight: 750,
                          fontSize: '1.1rem',
                          py: 0,
                        },
                      }}
                    />
                  )}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                Click a recording for its metrics; right-click for actions
                (replay, split, attach media, tag, disable)
              </Typography>
            </Stack>
            {drillInsightsPolling && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                icon={<CircularProgress size={12} thickness={6} />}
                label="Computing insights…"
                sx={{ ml: 1 }}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Mirrors the Activity page's toggle so both surfaces show the
                same set of a participant's captures. */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showDisabled}
                  onChange={(e) => setShowDisabled(e.target.checked)}
                />
              }
              label="Show disabled"
              slotProps={{ typography: { variant: 'caption' } }}
            />
          </Stack>
        </Stack>

        {/* Filters go BELOW the header row, not inside it. That row is a
          `direction="row" justifyContent="space-between"` holding the
          participant picker and the Show-disabled switch, so anything added
          to it becomes a third column — which is how the bar ended up
          wedged between them with its date chips wrapping onto their own
          line at a different left edge. Outside the row it gets the full
          width and one left edge, matching Activity. It also has to stay
          outside the results branch further down, so a filter that matches
          nothing cannot hide its own controls. */}
        {(allEvents.length > 0 || isFilterActive(filter)) && (
          <RecordingFilters
            value={filter}
            onChange={setFilter}
            options={filterOpts}
            // One participant, their own device — procedure and when are the
            // only questions left worth asking.
            show={{ participants: false, devices: false }}
            // `allEvents` is itself a filtered fetch now, so the old ratio
            // would compare a number against itself.
            summary={
              isFilterActive(filter)
                ? `${events.length} matching`
                : `${events.length} recordings`
            }
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {sessions === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : allEvents.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No captures recorded for this participant in this tenant yet.
          </Alert>
        ) : events.length === 0 ? (
          // Filtered to nothing. The bar above stays mounted (it is rendered
          // on the participant row, outside this branch) — hiding it here
          // would strand the operator with a filter they cannot undo, which
          // is what sent people to the browser's Back button and off the page
          // entirely.
          <Alert
            severity="info"
            variant="outlined"
            action={
              <Button
                size="small"
                onClick={() => setFilter({ ...EMPTY_FILTER })}
              >
                Clear filters
              </Button>
            }
          >
            No captures match the current filters.
          </Alert>
        ) : (
          <>
            {/* Master-detail: session picker (master, narrow left) beside
                the cognitive trace — the recording, its transcript and the
                EEG line, which is what people open a session to look at.
                The metrics and the AI-coach read on them sit underneath.
                On narrow widths everything stacks (picker first). */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                // Stretch so the picker rail matches the trace card
                // height (which drives the row); the rail scrolls
                // internally instead of driving the height.
                alignItems: 'stretch',
              }}
            >
              {/* Left: narrow session picker — stretched to the trace
                  card's height and scrolling internally. */}
              <Box
                sx={{
                  flex: { md: '0 0 188px' },
                  width: '100%',
                  minWidth: 0,
                  position: { md: 'relative' },
                  // Match the trace card when a session is selected;
                  // floor the rail when none is (right side is a hint).
                  minHeight: { md: selectedSession ? 0 : 460 },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    position: { xs: 'static', md: 'absolute' },
                    inset: { md: 0 },
                  }}
                >
                  <CaptureTimeline
                    events={shownEvents}
                    onSelect={handleSelect}
                    selectedId={selectedSessionId}
                    contextMenu
                    orientation="vertical"
                    fillHeight
                    {...actions}
                    emptyText="No captures recorded for this participant in this tenant yet."
                  />
                </Box>
              </Box>

              {/* Right: the selected session's cognitive trace. */}
              <Box
                sx={{
                  flex: { md: '1 1 0' },
                  width: '100%',
                  minWidth: 0,
                  alignSelf: 'flex-start',
                }}
              >
                {selectedSession ? (
                  <CognitiveTrace
                    sessionId={selectedSession.session_id}
                    sessionRowId={selectedSession.id}
                    tenantId={tenantId}
                    procedureName={selectedSession.procedure_name}
                    capturedAt={selectedSession.started_at}
                    durationS={_secondsBetweenSafe(
                      selectedSession.started_at,
                      selectedSession.ended_at,
                    )}
                    cognitiveScores={scores ?? []}
                    canRebin={canWrite}
                    canRecordDebrief={canOperateTenant(user, tenantId)}
                    participantLabel={selectedSession.participant_label}
                    onRebinApplied={reloadScores}
                    roleName={selectedSession.role_name ?? null}
                    canSetGo={canOperateTenant(user, tenantId)}
                    onGoSet={reloadScores}
                    pipeline={pipelines.get(selectedSession.session_id) ?? null}
                    chartAside={(pinnedT) => (
                      <HandTrajectoryPanel
                        key={selectedSession.id}
                        tenantId={tenantId}
                        sessionRowId={selectedSession.id}
                        tSec={pinnedT}
                        hideWhenEmpty
                      />
                    )}
                  />
                ) : (
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ py: 4, textAlign: 'center' }}
                  >
                    Select a session on the left to see its recording, trace
                    and metrics.
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Below: metrics for the session, with the AI Coach read on them
                (derived from the metrics vs the reference cohort) beside. */}
            {selectedSession && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ flex: { md: '1 1 0' }, width: '100%', minWidth: 0 }}>
                  <SessionMetricScorecard
                    tenantId={tenantId}
                    sessionId={selectedSession.session_id}
                  />
                </Box>
                <Box sx={{ flex: { md: '0 0 300px' }, width: '100%', minWidth: 0 }}>
                  <AiCoachCard
                    tenantId={tenantId}
                    sessionId={selectedSession.session_id}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {actionError}
          </Alert>
        )}
        {dialogs}
      </CardContent>
    </Card>
  );
}

function _secondsBetweenSafe(
  start: string | null | undefined,
  end: string | null | undefined,
): number | null {
  if (!start || !end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return null;
  return (e - s) / 1000;
}
